// Generic front-end pivot engine.
// Works on an array of flat record objects. Cells hold a Count by default, or
// a statistic (avg, median, ...) of a status duration when a measure is given.

import { RECORD_ID_FIELD, computeStat, durationField, roundDays } from "./measures";

const EMPTY = "—";

export const valOf = (record, field) => {
  const v = record[field];
  return v == null || v === "" ? EMPTY : String(v);
};

// Ordered, de-duplicated list of composite keys for a set of fields.
const buildKeyList = (records, fields) => {
  const map = new Map();
  records.forEach((r) => {
    const values = fields.map((f) => valOf(r, f));
    const key = values.join(" ▸ ");
    if (!map.has(key)) map.set(key, values);
  });
  return [...map.entries()]
    .sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }))
    .map(([key, values]) => ({ key, values }));
};

const ALL = "__all__";

/**
 * @param {Array<Object>} records
 * @param {string[]} rowFields
 * @param {string[]} colFields
 * @param {Object<string, Set<string>>} filters - field -> allowed values (empty/absent = all)
 * @param {{key: string, stat: string}} [measure] - a duration measure; omitted
 *   (or null) for Count. See computeMeasureCells.
 * @returns pivot model consumed by PivotGrid
 */
export const computePivot = (records, rowFields, colFields, filters = {}, measure = null) => {
  const filtered = records.filter((r) =>
    Object.entries(filters).every(
      ([f, set]) => !set || set.size === 0 || set.has(valOf(r, f))
    )
  );

  const rowKeys = rowFields.length
    ? buildKeyList(filtered, rowFields)
    : [{ key: ALL, values: [] }];
  const colKeys = colFields.length
    ? buildKeyList(filtered, colFields)
    : [{ key: ALL, values: [] }];

  const keyFor = (r, fields) =>
    fields.length ? fields.map((f) => valOf(r, f)).join(" ▸ ") : ALL;

  const axes = {
    rowFields,
    colFields,
    rowKeys,
    colKeys,
    isAllRow: rowFields.length === 0,
    isAllCol: colFields.length === 0,
  };

  if (measure) {
    return {
      ...axes,
      ...computeMeasureCells(filtered, measure, (r) => keyFor(r, rowFields), (r) =>
        keyFor(r, colFields)
      ),
    };
  }

  const cells = new Map();
  const rowTotals = new Map();
  const colTotals = new Map();
  let grandTotal = 0;

  filtered.forEach((r) => {
    const rk = keyFor(r, rowFields);
    const ck = keyFor(r, colFields);
    const cellKey = `${rk}||${ck}`;
    cells.set(cellKey, (cells.get(cellKey) || 0) + 1);
    rowTotals.set(rk, (rowTotals.get(rk) || 0) + 1);
    colTotals.set(ck, (colTotals.get(ck) || 0) + 1);
    grandTotal += 1;
  });

  return { ...axes, cells, rowTotals, colTotals, grandTotal };
};

/**
 * Cells for a duration measure. Every cell, row/column total and the grand
 * total is its OWN statistic over the raw durations it covers (a total is never
 * an average of averages), with `counts` holding how many reports fed each one.
 *
 * Reports are keyed by RECORD_ID_FIELD: a multi-value catalog splits one report
 * into several records, and a total must still count that report once.
 * Records with no duration (stage incomplete) still shape the axes but feed no
 * statistic; a cell without data is absent from `cells` (shown blank).
 */
const computeMeasureCells = (records, measure, rowKeyOf, colKeyOf) => {
  const field = durationField(measure.key);
  const cellVals = new Map();
  const rowVals = new Map();
  const colVals = new Map();
  const grandVals = new Map();

  const add = (bucket, key, id, value) => {
    let byId = bucket.get(key);
    if (!byId) bucket.set(key, (byId = new Map()));
    if (!byId.has(id)) byId.set(id, value);
  };

  const reports = new Set();
  records.forEach((r, i) => {
    const id = r[RECORD_ID_FIELD] ?? i;
    reports.add(id);
    const value = r[field];
    if (value == null) return;
    const rk = rowKeyOf(r);
    const ck = colKeyOf(r);
    add(cellVals, `${rk}||${ck}`, id, value);
    add(rowVals, rk, id, value);
    add(colVals, ck, id, value);
    if (!grandVals.has(id)) grandVals.set(id, value);
  });

  const statOf = (byId) => roundDays(computeStat([...byId.values()], measure.stat));
  const reduce = (bucket) => new Map([...bucket].map(([k, byId]) => [k, statOf(byId)]));
  const sizes = (bucket) => new Map([...bucket].map(([k, byId]) => [k, byId.size]));

  return {
    measure: { key: measure.key, stat: measure.stat },
    cells: reduce(cellVals),
    rowTotals: reduce(rowVals),
    colTotals: reduce(colVals),
    grandTotal: statOf(grandVals),
    counts: {
      cells: sizes(cellVals),
      rowTotals: sizes(rowVals),
      colTotals: sizes(colVals),
      grandTotal: grandVals.size,
    },
    // Reports in scope, with or without the stage: tells "nothing matched"
    // apart from "matched, but no report completed this stage".
    reportTotal: reports.size,
  };
};

/**
 * Whether a pivot has anything to show. A count pivot needs a non-zero total; a
 * measure pivot needs reports in scope (its grand total may be null, or 0 days).
 */
export const pivotHasData = (pivot) =>
  !!pivot && (pivot.measure ? pivot.reportTotal > 0 : pivot.grandTotal > 0);

/**
 * Adapts an EQL aggregate response into the same model PivotGrid consumes,
 * so the 1-axis (server-side) and 2-axis (client-side) paths render alike.
 *
 * The backend returns a flat, count-descending list; that order is preserved.
 *
 * @param {Array<{value: string|null, count: number}>} data
 * @param {string} rowField - dimension key placed on the row axis
 * @param {(value: string|null) => string} [labelOf] - value -> display label
 * @param {string} [emptyLabel] - label for the null/absent group (defaults to "—")
 */
export const pivotFromAggregate = (
  data,
  rowField,
  labelOf = (v) => v,
  emptyLabel = EMPTY
) => {
  const rows = (data || []).map((d) => ({
    label: d.value == null || d.value === "" ? emptyLabel : labelOf(d.value),
    count: d.count,
  }));

  const cells = new Map();
  const rowTotals = new Map();
  let grandTotal = 0;

  rows.forEach((r) => {
    // Distinct group values may collapse to the same label; sum them.
    cells.set(`${r.label}||${ALL}`, (cells.get(`${r.label}||${ALL}`) || 0) + r.count);
    rowTotals.set(r.label, (rowTotals.get(r.label) || 0) + r.count);
    grandTotal += r.count;
  });

  const rowKeys = [...rowTotals.keys()].map((label) => ({
    key: label,
    values: [label],
  }));

  return {
    rowFields: [rowField],
    colFields: [],
    rowKeys,
    colKeys: [{ key: ALL, values: [] }],
    cells,
    rowTotals,
    colTotals: new Map([[ALL, grandTotal]]),
    grandTotal,
    isAllRow: false,
    isAllCol: true,
  };
};

export const distinctValues = (records, field) => {
  const set = new Set();
  records.forEach((r) => set.add(valOf(r, field)));
  return [...set].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true })
  );
};
